import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { stub as $t } from '@nbcnews/analytics-framework';
import IntObserver from 'components/IntObserver';
import { getDataActivityMapID } from 'lib/articleUtils';
import RelatedItem from './Item';

import './styles.themed.scss';

/**
 * Related component displays a list of related items.
 * @param {object} props
 * @param {string} [props.additionalClasses] - Additional CSS classes for styling.
 * @param {boolean} [props.childOfRecommended] - Indicates if the component is part of recommended content.
 * @param {string} props.elementId - ID for the related items container.
 * @param {boolean} [props.isBetterNewsCommerce] - Flag for Better News Commerce styling.
 * @param {boolean} [props.isRightRail] - Flag for right rail placement.
 * @param {boolean} [props.isTrending] - Flag for trending content styling.
 * @param {boolean} [props.isNBCNewsCommerce] - Flag for NBC News Commerce styling.
 * @param {Array<object>} props.items - Array of related items to display, each item being an object.
 * @param {Function} props.linkTracking - Function for tracking link clicks.
 * @param {string} [props.pageRegion] - Region of the page where the component is displayed.
 * @param {string} props.title - Title of the related section.
 * @param {boolean} [props.isValidNonEcommerceTodayArticle] - Flag for valid non-ecommerce articles.
 */
const Related = ({
  additionalClasses = '',
  childOfRecommended = false,
  elementId,
  isBetterNewsCommerce = false,
  isRightRail = false,
  isTrending = false,
  isNBCNewsCommerce = false,
  items,
  linkTracking,
  pageRegion = 'article-body',
  title,
  isValidNonEcommerceTodayArticle = false,
}) => {
  const recRef = useRef();

  useEffect(() => {
    if (!isValidNonEcommerceTodayArticle) {
      $t('register', 'mbt_mynews_related_inview');
    }
  }, [isValidNonEcommerceTodayArticle]);


  /**
   * Handles the intersection observer callback to track when the related section is in view.
   * @param {IntersectionObserverEntry[]} e - Array of intersection observer entries.
   */
  const onView = (e) => {
    if (e?.[0]?.isIntersecting && !childOfRecommended) {
      $t('track', 'mbt_mynews_related_inview', {
        placement: 'body/right-rail',
      });
    }
  };

  const singleItem = items.length === 1;
  const isNotRightRail = !isRightRail;

  const dataActivityMapID = getDataActivityMapID(
    {
      componentName: 'related',
      pageRegion,
      componentTitle: title,
    },
  );

  return (
    <IntObserver threshold={0} callback={onView}>
      <section
        className={classNames(
          'related',
          {
            'related--trending': isTrending || isNBCNewsCommerce,
            'related--single': singleItem && isNotRightRail,
          },
          additionalClasses,
          'dn-print',
        )}
        data-activity-map={!childOfRecommended ? dataActivityMapID : null}
        data-testid="related"
        aria-label="Related content"
        ref={recRef}
      >
        <h3
          className={classNames({
            related__title: true,
            'related__title--trending': isTrending,
            'related__title--nbcnews-commerce': isNBCNewsCommerce,
            'related__title--betternews-commerce': isBetterNewsCommerce,
          })}
        >
          {title}
        </h3>
        <div
          id={elementId}
          className={classNames(
            'related__items',
            {
              'related__items--vertical': ((isTrending || isNBCNewsCommerce) && isRightRail),
              'related__items--nbcnews-commerce': isNBCNewsCommerce,
              'related__items--betternews-commerce': isBetterNewsCommerce,
            },
          )}
        >
          {items?.map((item) => (
            <RelatedItem
              key={item.title}
              item={item}
              linkTracking={linkTracking}
              singleItem={singleItem && isNotRightRail}
              isTrending={isTrending}
              isNBCNewsCommerce={isNBCNewsCommerce}
            />
          ))}
        </div>
      </section>
    </IntObserver>
  );
};

export default Related;
